import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  Loader2, Building2, CheckCircle, AlertTriangle, Trash2,
  DollarSign, TrendingUp, Clock, Mail, XCircle, Sparkles,
  Flame, HelpCircle, RefreshCw, Users, ArrowUpRight
} from 'lucide-react';
import { toast } from 'sonner';

/* ── Stat Card ── */
const StatCard = ({ label, value, subtext, icon: Icon, iconColor = 'text-muted-foreground', valueColor = '', onClick, testId }) => (
  <Card
    className={onClick ? 'cursor-pointer hover:ring-2 hover:ring-ring/30 transition-shadow' : ''}
    onClick={onClick}
    data-testid={testId}
  >
    <CardContent className="pt-5 pb-4 px-4">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide truncate">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${valueColor}`}>{value}</p>
          {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
        </div>
        <Icon className={`h-7 w-7 shrink-0 ${iconColor}`} />
      </div>
    </CardContent>
  </Card>
);

/* ── Section Header ── */
const SectionHeader = ({ title, children }) => (
  <div className="flex items-center justify-between mb-3 mt-8 first:mt-0">
    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{title}</h2>
    {children}
  </div>
);

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_tenants: 0,
    active_count: 0,
    inactive_count: 0,
    deleted_count: 0,
    total_mrr_cents: 0,
    comped_count: 0,
    active_comped_count: 0,
    eligible_trials_30d: null,
    converted_trials_30d: null,
    conversion_rate_30d: null,
    trials_in_progress: null,
    trial_emails_sent_7d: 0,
    trial_emails_attempted_7d: 0,
    trial_email_failures_7d: 0,
    tenants_in_drip: 0,
    tour_started_7d: 0,
    tour_completed_7d: 0,
    tour_total_started: 0,
    tour_total_completed: 0,
    tour_completion_rate_7d: null,
    tour_avg_steps: null,
  });
  const [revenueToday, setRevenueToday] = useState(null);
  const [recentSignups, setRecentSignups] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, revenueRes, tenantsRes] = await Promise.all([
        adminAPI.getTenantStats().catch(() => ({ data: null })),
        adminAPI.getPlatformRevenue().catch(() => ({ data: null })),
        adminAPI.getTenants({ page: 1, limit: 8, hide_test: true }).catch(() => ({ data: { tenants: [] } })),
      ]);
      if (statsRes.data) setStats(statsRes.data);
      if (revenueRes.data) setRevenueToday(revenueRes.data.today_cents);
      if (tenantsRes.data?.tenants) {
        const sorted = [...tenantsRes.data.tenants].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setRecentSignups(sorted.slice(0, 6));
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const fmtCents = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
  const fmtCents2 = (c) => c != null ? `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '\u2014';

  return (
    <div className="w-full max-w-full overflow-x-hidden" data-testid="admin-dashboard-page">
      <div className="px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Platform health and operational overview</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchData(); }} data-testid="dashboard-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* ── Section 1: Platform Health ── */}
        <SectionHeader title="Platform Health" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total Tenants" value={stats.total_tenants ?? 0} icon={Building2} iconColor="text-blue-500" testId="dash-total-tenants" />
          <StatCard
            label="Active"
            value={stats.active_count ?? 0}
            valueColor="text-green-600"
            subtext={stats.active_comped_count > 0 ? `${stats.active_comped_count} comped` : undefined}
            icon={CheckCircle}
            iconColor="text-green-500"
            testId="dash-active"
          />
          <StatCard label="Inactive" value={stats.inactive_count ?? 0} valueColor="text-yellow-600" icon={AlertTriangle} iconColor="text-yellow-500" testId="dash-inactive" />
          <StatCard label="Deleted" value={stats.deleted_count ?? 0} valueColor="text-red-600" icon={Trash2} iconColor="text-red-500" testId="dash-deleted" />
          <StatCard
            label="Monthly Income"
            value={`${fmtCents(stats.total_mrr_cents ?? 0)}/mo`}
            valueColor="text-emerald-600"
            subtext={stats.comped_count > 0 ? `${stats.comped_count} comped ($0)` : undefined}
            icon={DollarSign}
            iconColor="text-emerald-500"
            testId="dash-mrr"
          />
          <StatCard
            label="Platform Fees Today"
            value={fmtCents2(revenueToday)}
            valueColor="text-teal-600"
            subtext="Click for details"
            icon={TrendingUp}
            iconColor="text-teal-400"
            onClick={() => navigate('/admin/platform-revenue')}
            testId="dash-fees-today"
          />
        </div>

        {/* ── Section 2: Trial Funnel ── */}
        <SectionHeader title="Trial Funnel" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Eligible Trials (30+ days)"
            value={stats.eligible_trials_30d ?? '\u2014'}
            icon={Clock}
            iconColor="text-muted-foreground"
            testId="dash-eligible-trials"
          />
          <StatCard
            label="Trials In Progress"
            value={stats.trials_in_progress ?? '\u2014'}
            valueColor="text-amber-600"
            icon={Clock}
            iconColor="text-amber-400"
            testId="dash-trials-progress"
          />
          <StatCard
            label="Converted Trials"
            value={stats.converted_trials_30d ?? '\u2014'}
            valueColor="text-blue-600"
            icon={CheckCircle}
            iconColor="text-blue-500"
            testId="dash-converted"
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <StatCard
                    label="Trial \u2192 Paid %"
                    value={stats.conversion_rate_30d != null ? `${stats.conversion_rate_30d}%` : '\u2014'}
                    valueColor="text-indigo-600"
                    icon={TrendingUp}
                    iconColor="text-indigo-500"
                    testId="dash-conversion-rate"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[220px] text-xs">
                Only includes trials that have ended (30+ days). Cancellations later don't reduce this rate.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* ── Section 3: Onboarding & Engagement ── */}
        <SectionHeader title="Onboarding & Engagement" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <StatCard
            label="Trial Emails (7d)"
            value={`${stats.trial_emails_sent_7d ?? 0} / ${stats.trial_emails_attempted_7d ?? 0}`}
            icon={Mail}
            iconColor="text-teal-400"
            testId="dash-emails-7d"
          />
          <StatCard
            label="Email Failures (7d)"
            value={stats.trial_email_failures_7d ?? 0}
            valueColor={(stats.trial_email_failures_7d || 0) > 0 ? 'text-red-600' : 'text-muted-foreground'}
            icon={XCircle}
            iconColor="text-red-300"
            testId="dash-email-failures"
          />
          <StatCard
            label="Tenants In Drip"
            value={stats.tenants_in_drip ?? 0}
            valueColor="text-violet-600"
            icon={Mail}
            iconColor="text-violet-400"
            testId="dash-tenants-drip"
          />
          <StatCard
            label="Tours Started (7d)"
            value={stats.tour_started_7d ?? 0}
            valueColor="text-blue-600"
            subtext={`${stats.tour_total_started ?? 0} all-time`}
            icon={Sparkles}
            iconColor="text-blue-400"
            testId="dash-tours-started"
          />
          <StatCard
            label="Tours Completed (7d)"
            value={stats.tour_completed_7d ?? 0}
            valueColor="text-green-600"
            subtext={`${stats.tour_total_completed ?? 0} all-time`}
            icon={CheckCircle}
            iconColor="text-green-400"
            testId="dash-tours-completed"
          />
          <StatCard
            label="Completion Rate (7d)"
            value={stats.tour_completion_rate_7d != null ? `${stats.tour_completion_rate_7d}%` : '\u2014'}
            valueColor="text-indigo-600"
            icon={TrendingUp}
            iconColor="text-indigo-400"
            testId="dash-tour-rate"
          />
          <StatCard
            label="Avg. Steps Completed"
            value={stats.tour_avg_steps != null ? `${stats.tour_avg_steps}/5` : '\u2014'}
            valueColor="text-amber-600"
            icon={Flame}
            iconColor="text-amber-400"
            testId="dash-avg-steps"
          />
        </div>

        {/* ── Section 4: Recent Signups ── */}
        {recentSignups.length > 0 && (
          <>
            <SectionHeader title="Recent Signups">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tenants')} className="text-xs" data-testid="dash-view-all-tenants">
                View all <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </SectionHeader>
            <Card>
              <CardContent className="p-0">
                <div className="divide-y">
                  {recentSignups.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate('/admin/tenants')}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{t.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{t.owner_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            t.billing_status === 'active'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : t.billing_status === 'trial'
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-muted text-muted-foreground border-border'
                          }`}
                        >
                          {t.billing_status || 'unknown'}
                        </Badge>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
